import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Callout } from '@/features/local-mcp';
// Imported from the module, not the feature index: the index reaches the
// modals registry, which imports this file back before the index finishes.
// `Callout` can still come from the index because it is only used at render
import {
  describeLocalPath,
  describeLocalPathFailure,
  LOCAL_PATH_SETUPS,
  type TrustedLocalPathSetup,
} from '@/features/local-mcp/local-path-setup';

import {
  useLocalPathsConfiguration,
  type LocalPathFailure,
} from './use-local-paths-configuration';

import type { ConfigurationContext } from './configuration.types';
import type { ComponentType } from 'react';

interface PathsPresentation {
  itemNoun: string;
  addLabel: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  setup: TrustedLocalPathSetup;
  /** Renders the row label from the stored path. Omit to show the full
   * path; when set, the full path stays reachable via the row's title. */
  pathLabel?: (path: string) => string;
}

const FAILURE_ICON = <Icons.info className="size-4" />;

const FILESYSTEM_PATHS_CONFIG: PathsPresentation = {
  itemNoun: 'folder',
  addLabel: 'Add folder',
  description:
    'These folders set where Littlebird can look. What it can do inside them lives in Tools.',
  icon: Icons.folder,
  setup: LOCAL_PATH_SETUPS.filesystem,
};

const OBSIDIAN_PATHS_CONFIG: PathsPresentation = {
  itemNoun: 'vault',
  addLabel: 'Add vault',
  description:
    'These vaults set where Littlebird can look. What it can do inside them lives in Tools.',
  icon: Icons.book,
  setup: LOCAL_PATH_SETUPS.obsidian,
  // A vault is named by its folder; the rest of the path is noise here.
  pathLabel: (path) => describeLocalPath(path).name,
};

function ConfigurationUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <YStack className="items-center gap-3 p-10 text-center">
      <Icons.warn className="size-5 text-foreground-muted" />
      <p className="text-sm text-foreground-muted">
        Couldn&apos;t read this integration from Littlebird&apos;s local
        settings.
      </p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </YStack>
  );
}

function LocalPathsList({
  paths,
  config,
  failure,
  saving,
  onAdd,
  onRemove,
}: {
  paths: string[];
  config: PathsPresentation;
  failure: LocalPathFailure | null;
  saving: boolean;
  onAdd: () => void;
  onRemove: (path: string) => void;
}) {
  return (
    <YStack className="gap-4 p-6">
      <p className="text-xs text-foreground-muted">{config.description}</p>

      <YStack className="overflow-hidden rounded-lg border border-border bg-surface">
        <YStack className="py-1">
          {paths.map((path) => (
            <XStack
              key={path}
              data-path={path}
              className="items-center justify-between gap-3 px-4 py-2"
            >
              <XStack className="min-w-0 flex-1 items-center gap-3">
                {failure?.path === path ? (
                  <Icons.warn
                    role="img"
                    aria-label={`${describeLocalPath(path).name} needs attention`}
                    className="size-4 shrink-0 text-danger-11"
                  />
                ) : (
                  <config.icon className="size-4 shrink-0 text-foreground-muted" />
                )}
                <span
                  title={config.pathLabel ? path : undefined}
                  className="min-w-0 flex-1 truncate font-mono text-sm text-foreground"
                >
                  {config.pathLabel ? config.pathLabel(path) : path}
                </span>
              </XStack>
              <button
                type="button"
                aria-label={`Remove ${describeLocalPath(path).name}`}
                disabled={saving}
                onClick={() => onRemove(path)}
                className="rounded-md p-1.5 text-foreground-muted transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
              >
                <Icons.trash className="size-3.5" />
              </button>
            </XStack>
          ))}
          {paths.length === 0 && (
            <p className="px-4 py-2 text-xs text-foreground-muted">
              No {config.itemNoun}s allowed yet.
            </p>
          )}
        </YStack>
        <button
          type="button"
          onClick={onAdd}
          disabled={saving}
          className="flex w-full items-center gap-2 border-t border-border px-4 py-3 text-left text-sm text-foreground-muted transition-colors hover:bg-surface-hover hover:text-foreground disabled:opacity-50"
        >
          <Icons.plus className="size-4" />
          {config.addLabel}
        </button>
      </YStack>

      {failure ? (
        <div role="alert">
          <Callout tone="danger" icon={FAILURE_ICON}>
            {describeLocalPathFailure(config.setup, failure)}
          </Callout>
        </div>
      ) : null}
    </YStack>
  );
}

function LocalPathsConfiguration({
  context,
  config,
}: {
  context: ConfigurationContext;
  config: PathsPresentation;
}) {
  const serverSlug =
    context.localSource?.kind === 'local_mcp'
      ? context.localSource.serverSlug
      : undefined;
  const { serversQuery, server, paths, failure, saving, addPath, removePath } =
    useLocalPathsConfiguration(serverSlug);

  if (serversQuery.isPending) return null;
  if (serversQuery.isError || !server) {
    return (
      <ConfigurationUnavailable onRetry={() => void serversQuery.refetch()} />
    );
  }

  return (
    <LocalPathsList
      paths={paths}
      config={config}
      failure={failure}
      saving={saving}
      onAdd={addPath}
      onRemove={removePath}
    />
  );
}

export function FilesystemFoldersConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  return (
    <LocalPathsConfiguration
      context={context}
      config={FILESYSTEM_PATHS_CONFIG}
    />
  );
}

export function ObsidianVaultsConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  return (
    <LocalPathsConfiguration context={context} config={OBSIDIAN_PATHS_CONFIG} />
  );
}
